/**
 * The patient record.
 *
 * Modelled as a thin, FHIR-shaped projection rather than a full clinical record:
 * every field here exists because some class of eligibility criterion reads it.
 * That constraint is what keeps the schema small enough to reason about, and it is
 * also a privacy control — data minimization by construction rather than by policy.
 *
 * Identity separation is the structural privacy decision. `PatientIdentity` holds
 * the direct identifiers and lives in its own Firestore collection that the
 * sponsor roles cannot read at all; `Patient` holds the clinical facts and carries
 * no name. A sponsor query can therefore return a real count and a real
 * pseudonymous ID without any code path existing that could return a name.
 * See `DECISIONS.md` D-16.
 */
import { z } from 'zod'
import {
  DataSourceTypeSchema,
  EthnicitySchema,
  GenderIdentitySchema,
  RaceSchema,
  SexSchema,
} from './vocab.js'
import {
  CodingSchema,
  GeoPointSchema,
  IsoDateSchema,
  IsoInstantSchema,
  QuantitySchema,
  idSchema,
} from './zod-util.js'
import type { DocumentId, OrgId, PatientId, PseudonymId, UserId } from './ids.js'

/**
 * Provenance carried by every clinical fact.
 *
 * Attached per-fact, not per-record, because a single patient's chart mixes
 * structured lab feeds with facts scraped out of narrative notes, and the evidence
 * table has to be able to tell a coordinator which is which. A hemoglobin value
 * from a lab interface and one parsed from a sentence deserve different trust.
 */
export const ProvenanceSchema = z.object({
  source: DataSourceTypeSchema,
  /** The document this fact was extracted from, when it came from text. */
  documentId: idSchema<DocumentId>('document id').optional(),
  /** Character offsets into that document, so the UI can highlight the span. */
  span: z.object({ start: z.number().int().nonnegative(), end: z.number().int().nonnegative() }).optional(),
  /** Extraction confidence for parsed facts; absent for structured ones, which are certain. */
  confidence: z.number().min(0).max(1).optional(),
  recordedAt: IsoInstantSchema,
})
export type Provenance = z.infer<typeof ProvenanceSchema>

/**
 * Direct identifiers — the 18 HIPAA Safe Harbor categories live here and nowhere
 * else. Separate collection, separate security rules, never joined into a match
 * result. See `firestore.ts` for the access rules.
 */
export const PatientIdentitySchema = z.object({
  patientId: idSchema<PatientId>('patient id'),
  /** The Firebase Auth UID, when the patient has an account of their own. */
  userId: idSchema<UserId>('user id').optional(),
  givenName: z.string().min(1),
  familyName: z.string().min(1),
  /** Full birth date. Only `Patient.birthYear` and a computed age cross into match logic. */
  birthDate: IsoDateSchema,
  email: z.string().email().optional(),
  phone: z.string().optional(),
  /** Medical record number, scoped to the owning health system. */
  mrn: z.string().optional(),
  addressLine: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zip: z.string().optional(),
  /**
   * Every patient in this repository is generated. The flag is stored on the
   * record rather than assumed globally so that any future real-data path has to
   * make an explicit, greppable, reviewable decision to set it false.
   */
  isSynthetic: z.literal(true),
})
export type PatientIdentity = z.infer<typeof PatientIdentitySchema>

/** A diagnosis. */
export const ConditionSchema = z.object({
  code: CodingSchema,
  /** The label as it appeared in the source system, which is what the clinician recognizes. */
  display: z.string().min(1),
  onsetDate: IsoDateSchema.optional(),
  abatementDate: IsoDateSchema.optional(),
  clinicalStatus: z.enum(['ACTIVE', 'RECURRENCE', 'RELAPSE', 'INACTIVE', 'REMISSION', 'RESOLVED', 'UNKNOWN']).default('ACTIVE'),
  /**
   * Whether this is the patient's own condition or a family member's.
   *
   * Family history sits in the same section of many charts and is a classic
   * source of false matches: "family history of breast cancer" must never satisfy
   * "histologically confirmed breast cancer". Storing it as a field rather than
   * relying on the extractor to have dropped it makes the mistake catchable.
   */
  subject: z.enum(['PATIENT', 'FAMILY']).default('PATIENT'),
  /** Cancer stage, when applicable. Free text because staging notation is not uniform. */
  stage: z.string().optional(),
  bodySite: z.string().optional(),
  provenance: ProvenanceSchema,
})
export type Condition = z.infer<typeof ConditionSchema>

/** A lab result or vital sign. */
export const ObservationSchema = z.object({
  code: CodingSchema,
  display: z.string().min(1),
  /** Numeric result with its unit. Absent for qualitative results, which use `valueString`. */
  value: QuantitySchema.optional(),
  valueString: z.string().optional(),
  /** Reference range from the performing lab, needed to interpret "within normal limits" criteria. */
  referenceLow: z.number().optional(),
  referenceHigh: z.number().optional(),
  interpretation: z.enum(['NORMAL', 'LOW', 'HIGH', 'CRITICAL_LOW', 'CRITICAL_HIGH', 'ABNORMAL', 'UNKNOWN']).default('UNKNOWN'),
  effectiveDate: IsoDateSchema,
  provenance: ProvenanceSchema,
})
export type Observation = z.infer<typeof ObservationSchema>

/** A medication the patient is on or has taken. */
export const MedicationSchema = z.object({
  code: CodingSchema,
  display: z.string().min(1),
  status: z.enum(['ACTIVE', 'COMPLETED', 'STOPPED', 'ON_HOLD', 'UNKNOWN']).default('ACTIVE'),
  startDate: IsoDateSchema.optional(),
  endDate: IsoDateSchema.optional(),
  /** ATC class, which is how washout and drug-class exclusions are usually written. */
  atcClass: z.string().optional(),
  /** True for antineoplastic therapy, which drives prior-line counting in oncology trials. */
  isAntineoplastic: z.boolean().default(false),
  provenance: ProvenanceSchema,
})
export type Medication = z.infer<typeof MedicationSchema>

/** A procedure or surgery. */
export const ProcedureSchema = z.object({
  code: CodingSchema,
  display: z.string().min(1),
  performedDate: IsoDateSchema.optional(),
  provenance: ProvenanceSchema,
})
export type Procedure = z.infer<typeof ProcedureSchema>

/**
 * A genomic finding. Flat and small on purpose — full variant representation is a
 * project of its own, and the criteria we need to satisfy read at the level of
 * "does this patient have an activating EGFR exon 19 deletion".
 */
export const BiomarkerSchema = z.object({
  gene: z.string().min(1),
  /** HGVS notation when available, e.g. `p.L858R`. */
  variant: z.string().optional(),
  /** OncoTree/NCIt-style alteration category, e.g. `DELETION`, `AMPLIFICATION`, `FUSION`. */
  alterationType: z.enum(['SNV', 'INDEL', 'DELETION', 'AMPLIFICATION', 'FUSION', 'EXPRESSION', 'MSI', 'TMB', 'OTHER']).optional(),
  result: z.enum(['POSITIVE', 'NEGATIVE', 'INDETERMINATE', 'NOT_TESTED']),
  /** Quantitative result for continuous assays (TMB mut/Mb, PD-L1 TPS %). */
  value: QuantitySchema.optional(),
  assay: z.string().optional(),
  testedDate: IsoDateSchema.optional(),
  provenance: ProvenanceSchema,
})
export type Biomarker = z.infer<typeof BiomarkerSchema>

/**
 * A clinical note. Synthetic in this repo, and the substrate for the extraction
 * and evidence-span machinery — a matcher that only reads structured fields cannot
 * answer most real eligibility criteria, so notes are not optional scenery.
 */
export const ClinicalDocumentSchema = z.object({
  id: idSchema<DocumentId>('document id'),
  patientId: idSchema<PatientId>('patient id'),
  type: z.enum(['PROGRESS_NOTE', 'DISCHARGE_SUMMARY', 'PATHOLOGY_REPORT', 'RADIOLOGY_REPORT', 'CONSULT_NOTE', 'GENOMIC_REPORT', 'OTHER']),
  title: z.string().default(''),
  text: z.string(),
  authoredDate: IsoDateSchema,
  /** Always `SYNTHETIC` in this repository. */
  source: DataSourceTypeSchema.default('SYNTHETIC'),
})
export type ClinicalDocument = z.infer<typeof ClinicalDocumentSchema>

/**
 * Performance status. Kept as its own field rather than as an observation because
 * a large fraction of oncology trials gate on it and it is worth making
 * impossible to miss.
 */
export const PerformanceStatusSchema = z.object({
  /** ECOG 0–5. */
  ecog: z.number().int().min(0).max(5).optional(),
  /** Karnofsky 0–100, which some protocols use instead. */
  karnofsky: z.number().int().min(0).max(100).optional(),
  assessedDate: IsoDateSchema.optional(),
  provenance: ProvenanceSchema.optional(),
})
export type PerformanceStatus = z.infer<typeof PerformanceStatusSchema>

/**
 * The de-identified clinical record. This is the object the matcher consumes.
 *
 * Note what is absent: no name, no full birth date, no address, no MRN. Age is
 * carried as a year and a computed age; location as a ZIP3 and an approximate
 * geopoint, which is enough for a site-distance feature and not enough to find a
 * house.
 */
export const PatientSchema = z.object({
  id: idSchema<PatientId>('patient id'),
  /** Stable pseudonym shown to sponsors in place of any identifier. */
  pseudonym: idSchema<PseudonymId>('pseudonym'),
  /** The health system that holds this record. The tenancy boundary. */
  orgId: idSchema<OrgId>('org id'),

  // --- demographics, minimized ---
  birthYear: z.number().int().min(1900).max(2030),
  /** Age in years at `factsAsOf`. Precomputed so the matcher never needs the birth date. */
  ageYears: z.number().int().min(0).max(130),
  sex: SexSchema,
  genderIdentity: GenderIdentitySchema.default('UNKNOWN'),
  race: z.array(RaceSchema).default([]),
  ethnicity: EthnicitySchema.default('UNKNOWN'),
  /** BCP-47 preferred language, e.g. `en`, `es`. Drives language-access features. */
  preferredLanguage: z.string().default('en'),
  /** First three ZIP digits only — Safe Harbor permits ZIP3 for populations over 20,000. */
  zip3: z.string().regex(/^\d{3}$/).optional(),
  /** Approximate location for distance features. Centroid of the ZIP3, not the home. */
  approximateGeo: GeoPointSchema.optional(),
  /** True if the patient is pregnant, which many trials exclude outright. */
  isPregnant: z.boolean().optional(),

  // --- clinical facts ---
  conditions: z.array(ConditionSchema).default([]),
  observations: z.array(ObservationSchema).default([]),
  medications: z.array(MedicationSchema).default([]),
  procedures: z.array(ProcedureSchema).default([]),
  biomarkers: z.array(BiomarkerSchema).default([]),
  performanceStatus: PerformanceStatusSchema.optional(),
  allergies: z.array(z.object({ display: z.string(), code: CodingSchema.optional() })).default([]),

  /** Count of prior systemic therapy lines. Precomputed because deriving it is subtle and reused. */
  priorTherapyLines: z.number().int().nonnegative().default(0),

  /**
   * As-of date for every fact in this record.
   *
   * Load-bearing: a record whose newest fact is fourteen months old should not
   * produce a confident eligibility verdict, and the reranker gets record
   * staleness as an explicit feature rather than being left to infer it.
   */
  factsAsOf: IsoDateSchema,
  /** The treating clinicians who may see this patient in their panel. */
  careTeamUserIds: z.array(idSchema<UserId>('user id')).default([]),

  updatedAt: IsoInstantSchema,
})
export type Patient = z.infer<typeof PatientSchema>

// ---------------------------------------------------------------------------
// Derived helpers — shared so the web app, Functions, and seed data agree
// ---------------------------------------------------------------------------

/** Age in whole years on a given date. */
export function ageOn(birthDate: string, asOf: string): number {
  const b = new Date(`${birthDate}T00:00:00Z`)
  const a = new Date(`${asOf}T00:00:00Z`)
  let age = a.getUTCFullYear() - b.getUTCFullYear()
  const monthDelta = a.getUTCMonth() - b.getUTCMonth()
  if (monthDelta < 0 || (monthDelta === 0 && a.getUTCDate() < b.getUTCDate())) age -= 1
  return Math.max(0, age)
}

/** Whole days between two ISO dates. Negative when `to` precedes `from`. */
export function daysBetween(from: string, to: string): number {
  const ms = new Date(`${to}T00:00:00Z`).getTime() - new Date(`${from}T00:00:00Z`).getTime()
  return Math.round(ms / 86_400_000)
}

/**
 * Most recent observation for a LOINC code.
 *
 * Recency matters clinically — an ANC from eight months ago says nothing about
 * whether the patient meets a neutrophil threshold today — so this returns the
 * latest and the caller decides whether it is fresh enough.
 */
export function latestObservation(patient: Patient, loincCode: string): Observation | null {
  let latest: Observation | null = null
  for (const obs of patient.observations) {
    if (obs.code.code !== loincCode) continue
    if (!latest || obs.effectiveDate > latest.effectiveDate) latest = obs
  }
  return latest
}

/** Active conditions belonging to the patient, excluding family history. */
export function activeConditions(patient: Patient): Condition[] {
  return patient.conditions.filter(
    (c) => c.subject === 'PATIENT' && (c.clinicalStatus === 'ACTIVE' || c.clinicalStatus === 'RECURRENCE' || c.clinicalStatus === 'RELAPSE')
  )
}

/** Currently-active medications. */
export function activeMedications(patient: Patient): Medication[] {
  return patient.medications.filter((m) => m.status === 'ACTIVE')
}

/**
 * Days since the newest fact in the record. Used directly as a reranker feature
 * and to show a "record last updated" warning in the UI.
 */
export function recordStalenessDays(patient: Patient, asOf: string): number {
  const dates: string[] = [patient.factsAsOf]
  for (const o of patient.observations) dates.push(o.effectiveDate)
  for (const c of patient.conditions) if (c.onsetDate) dates.push(c.onsetDate)
  const newest = dates.reduce((max, d) => (d > max ? d : max), dates[0] ?? asOf)
  return Math.max(0, daysBetween(newest, asOf))
}

/**
 * Whether a biomarker is positive in the patient, treating an untested gene as
 * unknown rather than negative.
 *
 * That distinction is the whole point: "not tested" and "tested negative" mean
 * completely different things for a biomarker-gated trial. Returning `null` for
 * untested forces callers to handle it, which is how the pipeline ends up
 * emitting `INSUFFICIENT_INFORMATION` and telling the coordinator to order the
 * test — a genuinely useful output — instead of quietly excluding the patient.
 */
export function biomarkerStatus(patient: Patient, gene: string): boolean | null {
  const hits = patient.biomarkers.filter((b) => b.gene.toUpperCase() === gene.toUpperCase())
  if (hits.length === 0) return null
  if (hits.some((b) => b.result === 'POSITIVE')) return true
  if (hits.every((b) => b.result === 'NOT_TESTED' || b.result === 'INDETERMINATE')) return null
  return false
}
